import { type FC, useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Smartphone,
  Library,
  Calendar,
  CalendarClock,
  Bot,
  BarChart3,
  Brain,
  HardDrive,
  Bell,
  Activity,
  Shield,
  HeartPulse,
  Radio,
  Settings,
  LifeBuoy,
  ChevronLeft,
  ChevronRight,
  Search,
  Pin,
  PinOff,
  History,
  SmartphoneNfc,
  FolderOpen,
  Upload,
  FolderPlus,
  ClipboardCheck,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { useUIStore } from '../../store/ui-store.js';
import { useAuthStore } from '../../store/auth-store.js';
import { useV1Role } from '../../hooks/use-v1-role.js';
import { Avatar, AvatarFallback } from '../ui/avatar.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip.js';
import { Input } from '../ui/input.js';
import { DEVICE_CONTROL_ENABLED, DEVICE_CONTROL_ROUTES } from '../../lib/features.js';

interface NavItem {
  path: string;
  label: string;
  icon: FC<{ className?: string }>;
  section: string;
  /** Only these v1 roles see the item (unset: decided by the role filter below). */
  roles?: readonly string[];
}

/** Roles the server lets list agency content approvals (content.view) and act on them. */
const AGENCY_STAFF = ['AGENCY_OWNER', 'ACCOUNT_MANAGER', 'CONTENT_MANAGER'] as const;

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, section: 'Overview' },
  { path: '/hub', label: 'Content Hub', icon: FolderOpen, section: 'Content' },
  { path: '/hub/content', label: 'Content Library', icon: Library, section: 'Content' },
  { path: '/hub/upload', label: 'Upload', icon: Upload, section: 'Content' },
  { path: '/hub/collections', label: 'Collections', icon: FolderPlus, section: 'Content' },
  { path: '/content/approvals', label: 'Content Approvals', icon: ClipboardCheck, section: 'Content', roles: AGENCY_STAFF },
  { path: '/creator/content-approvals', label: 'Approve My Content', icon: ClipboardCheck, section: 'Content', roles: ['CREATOR'] },
  { path: '/creators', label: 'Creators', icon: Users, section: 'Management' },
  { path: '/devices', label: 'Devices', icon: Smartphone, section: 'Management' },
  { path: '/agents', label: 'Agents', icon: Bot, section: 'Management' },
  { path: '/content', label: 'Legacy Content', icon: Library, section: 'Management' },
  { path: '/media', label: 'Media Library', icon: Library, section: 'Management' },
  { path: '/posts', label: 'Scheduled Posts', icon: CalendarClock, section: 'Management' },
  { path: '/scheduler', label: 'Calendar View', icon: Calendar, section: 'Management' },
  { path: '/automation', label: 'Automation', icon: Bot, section: 'Management' },
  { path: '/analytics', label: 'Analytics', icon: BarChart3, section: 'Intelligence' },
  { path: '/ai', label: 'AI Intelligence', icon: Brain, section: 'Intelligence' },
  { path: '/storage', label: 'Storage', icon: HardDrive, section: 'Operations' },
  { path: '/notifications', label: 'Notifications', icon: Bell, section: 'Operations' },
  { path: '/activity', label: 'Activity Logs', icon: Activity, section: 'Operations' },
  { path: '/integrity', label: 'Content Integrity', icon: Shield, section: 'Operations' },
  { path: '/health', label: 'System Health', icon: HeartPulse, section: 'System' },
  { path: '/streaming', label: 'Streaming', icon: Radio, section: 'System' },
  { path: '/settings', label: 'Settings', icon: Settings, section: 'System' },
];

const sectionOrder = ['Overview', 'Content', 'Management', 'Intelligence', 'Operations', 'System'];

// Device-control pages are hidden while the kill switch is false; the Devices
// registry page stays (claiming and creator assignment are not control).
// Evaluated per render (not at module load) so the switch can be mocked.
function availableNavItems(): NavItem[] {
  return DEVICE_CONTROL_ENABLED
    ? navItems
    : navItems.filter((item) => !DEVICE_CONTROL_ROUTES.includes(item.path));
}

export const Sidebar: FC = () => {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar, pinnedNavItems, pinNavItem, unpinNavItem, recentPages, addRecentPage } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const { v1Role } = useV1Role();
  const [searchQuery, setSearchQuery] = useState('');

  const roleFilteredItems = useMemo(() => {
    const items = availableNavItems().filter(
      (item) => item.roles === undefined || (v1Role !== null && item.roles.includes(v1Role)),
    );
    if (v1Role === 'CREATOR') {
      return items.filter((item) =>
        ['/', '/creators', '/settings', '/audit', '/hub', '/hub/content', '/hub/upload', '/hub/collections', '/content', '/media', '/creator/content-approvals'].includes(item.path),
      );
    }
    if (v1Role === 'VIEWER') {
      return items.filter((item) => ['/', '/activity', '/settings'].includes(item.path));
    }
    return items;
  }, [v1Role]);

  const filteredItems = useMemo(() => {
    if (searchQuery.length === 0) return roleFilteredItems;
    const q = searchQuery.toLowerCase();
    return roleFilteredItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.path.toLowerCase().includes(q) ||
        item.section.toLowerCase().includes(q),
    );
  }, [roleFilteredItems, searchQuery]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, NavItem[]> = {};
    const pinned: NavItem[] = [];

    for (const item of filteredItems) {
      if (pinnedNavItems.includes(item.path)) {
        pinned.push(item);
        continue;
      }
      if (groups[item.section] === undefined) groups[item.section] = [];
      groups[item.section]!.push(item);
    }

    return { pinned, groups };
  }, [filteredItems, pinnedNavItems]);

  const handleNavClick = (path: string, label: string) => {
    addRecentPage(path, label);
  };

  return (
    <aside
      className={cn(
        'flex flex-col border-r border-slate-800 bg-slate-900/95 backdrop-blur-sm transition-all duration-200',
        sidebarCollapsed ? 'w-14' : 'w-56',
      )}
    >
      {/* Brand */}
      <div className={cn('flex h-14 items-center border-b border-slate-800 px-3', sidebarCollapsed ? 'justify-center' : 'justify-between')}>
        {!sidebarCollapsed && (
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
              <SmartphoneNfc className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-slate-100">LaxmiAI</span>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
            <SmartphoneNfc className="h-4 w-4 text-white" />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="flex h-6 w-6 items-center justify-center rounded-md text-slate-500 hover:bg-slate-800 hover:text-slate-300"
        >
          {sidebarCollapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Search */}
      {!sidebarCollapsed && (
        <div className="px-3 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search nav..."
              className="h-8 w-full rounded-lg border border-slate-800 bg-slate-800/30 pl-8 pr-3 text-xs text-slate-300 placeholder:text-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-2 scrollbar-thin">
        {/* Pinned */}
        {groupedItems.pinned.length > 0 && !sidebarCollapsed && (
          <div className="mb-2">
            <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Pinned
            </div>
            {groupedItems.pinned.map((item) => (
              <NavItemRow
                key={item.path}
                item={item}
                collapsed={sidebarCollapsed}
                isPinned
                onPin={() => unpinNavItem(item.path)}
                onNavigate={() => handleNavClick(item.path, item.label)}
              />
            ))}
            <div className="my-2 border-t border-slate-800" />
          </div>
        )}

        {/* Sections */}
        {searchQuery.length > 0 ? (
          <div className="space-y-0.5">
            {filteredItems.map((item) => (
              <NavItemRow
                key={item.path}
                item={item}
                collapsed={sidebarCollapsed}
                isPinned={pinnedNavItems.includes(item.path)}
                onPin={() => {
                  if (pinnedNavItems.includes(item.path)) {
                    unpinNavItem(item.path);
                  } else {
                    pinNavItem(item.path);
                  }
                }}
                onNavigate={() => handleNavClick(item.path, item.label)}
              />
            ))}
          </div>
        ) : (
          sectionOrder.map((section) => {
            const items = groupedItems.groups[section];
            if (items === undefined || items.length === 0) return null;
            return (
              <div key={section} className="mb-3">
                {!sidebarCollapsed && (
                  <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                    {section}
                  </div>
                )}
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <NavItemRow
                      key={item.path}
                      item={item}
                      collapsed={sidebarCollapsed}
                      isPinned={false}
                      onPin={() => pinNavItem(item.path)}
                      onNavigate={() => handleNavClick(item.path, item.label)}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}

        {/* Recent Pages */}
        {recentPages.length > 0 && !sidebarCollapsed && searchQuery.length === 0 && (
          <div className="mt-4">
            <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Recent
            </div>
            <div className="space-y-0.5">
              {recentPages.slice(0, 4).map((page) => (
                <NavLink
                  key={page.path}
                  to={page.path}
                  onClick={() => handleNavClick(page.path, page.label)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-slate-500 transition-colors hover:bg-slate-800 hover:text-slate-300',
                      isActive && 'text-slate-100',
                    )
                  }
                >
                  <History className="h-3 w-3" />
                  {page.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* User footer */}
      {!sidebarCollapsed && (
        <div className="border-t border-slate-800 p-3">
          <div className="flex items-center gap-2">
            <Avatar className="h-7 w-7">
              <AvatarFallback>{(user?.name ?? 'U').charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-300">{user?.name ?? 'User'}</p>
              <p className="truncate text-[10px] text-slate-600">{user?.email ?? ''}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

// Individual nav item row
const NavItemRow: FC<{
  item: NavItem;
  collapsed: boolean;
  isPinned: boolean;
  onPin: () => void;
  onNavigate: () => void;
}> = ({ item, collapsed, isPinned, onPin, onNavigate }) => {
  const Icon = item.icon;

  const link = (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center rounded-md text-sm transition-colors',
          collapsed ? 'justify-center px-2 py-2' : 'gap-2.5 px-2 py-1.5',
          isActive
            ? 'bg-blue-600/10 text-blue-400'
            : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <div className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-blue-500" />
          )}
          <Icon className={cn('h-4 w-4 shrink-0', collapsed && 'h-5 w-5')} />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {!collapsed && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPin();
              }}
              className="ml-auto opacity-0 transition-opacity group-hover:opacity-100"
            >
              {isPinned ? (
                <PinOff className="h-3 w-3 text-slate-600 hover:text-slate-400" />
              ) : (
                <Pin className="h-3 w-3 text-slate-600 hover:text-slate-400" />
              )}
            </button>
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
};
